import { useState } from 'react';
import { CellContext } from '@tanstack/react-table';
import { outreach, outreachQueries } from '@round/api';
import {
    Badge,
    ComboboxExternal,
    ComboboxItem,
    lucideIcons,
    Popover,
    Skeleton,
    tableHelpers,
    toast,
    Tooltip,
} from '@round/creatorbase-ui';
import { useClientContentTags, useUpdateOutreachResultMutation } from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import useOverflowingChildren from 'Hooks/useOverflowingChildren';
import { mapContentTagsToOptions } from 'helpers';
import { usePatchOutreachResultsCache } from '../../../../hooks/usePatchOutreachResultsCache';
import { getMeta, NO_VALUE, OutreachResultRow } from '../../../../helpers';
import styles from './TagsCell.module.css';

const { Pencil } = lucideIcons;

const updateUtils = outreachQueries.outreachRequestResults.utils.update;

export const EDIT_TAGS_LABEL = 'Edit tags';
const NO_TAGS_LABEL = 'No tags';
const SEARCH_PLACEHOLDER = 'Search tags…';
const NO_TAGS_FOUND = 'No tags found';
const LOADING = 'Loading...';

const OVERFLOW_BADGE_RESERVE_PX = 40;

const PICKER_LIST_SELECTOR = '[data-slot="combobox-content"]';
const FIELD_SELECTOR = '[data-slot="popover-trigger"]';

const isInside = (target: EventTarget | null, selector: string) => (target as Element | null)?.closest(selector);

/**
 * A pointerdown outside the popover closes it, but the click that same press produces still goes
 * where it landed: a row, which would expand, or a button. Eating that one click keeps closing the
 * picker from doing anything but closing it. Let go again on the next press, in case this one
 * never becomes a click.
 */
const swallowNextClick = () => {
    const swallow = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        release();
    };
    const release = () => {
        document.removeEventListener('click', swallow, true);
        document.removeEventListener('pointerdown', release, true);
    };
    document.addEventListener('click', swallow, true);
    document.addEventListener('pointerdown', release, true);
};

type TagItem = ComboboxItem<number>;

type Props = CellContext<tableHelpers.WithLoadingRow<OutreachResultRow>, unknown>;

/**
 * The tags a reviewer puts on a result, which reach the creator when the result is added. Editable
 * only while the result is in review: after that the tags are the creator's, and this is a record of
 * what was applied. The row is the only source the chips read from, so a set that arrives already
 * filled (a request that collected tags, say) shows the same way as one the reviewer picked.
 */
export const TagsCell = ({ row: { original }, table }: Props) => {
    const { isLoading } = getMeta(table);
    const { selectedClientId } = useClientSelect();
    const { tags, isLoading: areTagsLoading } = useClientContentTags(selectedClientId);
    const patchResultsCache = usePatchOutreachResultsCache();
    const { update, isLoading: isSaving } = useUpdateOutreachResultMutation(original.id);

    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<TagItem[]>([]);

    if (tableHelpers.isLoadingRow(original) || isLoading) {
        return <Skeleton className={styles.skeleton} />;
    }

    const options = mapContentTagsToOptions(tags);
    const toItems = (ids: number[]) =>
        ids.flatMap((id) => {
            const option = options.find((option) => option.value === id);
            return option ? [option] : [];
        });

    const items = toItems(original.content_tags);

    if (original.status !== 'TO_REVIEW') {
        return <TagChips items={items} />;
    }

    const id = original.id;

    const isSameSet = (a: number[], b: number[]) => a.length === b.length && a.every((value) => b.includes(value));

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);

        if (open) {
            setDraft(toItems(original.content_tags));
            return;
        }

        const content_tags = draft.map((item) => item.value);
        if (isSameSet(content_tags, original.content_tags)) {
            return;
        }

        update(
            { id, data: { content_tags } },
            {
                onSuccess: (result: outreach.OutreachResult) => patchResultsCache([result]),
                onError: (error) => toast.error(updateUtils.getErrorMessage(error)),
            }
        );
    };

    return (
        // The popover portals its content, so a click inside it still reaches the row's onRowClick
        // via React's tree-based bubbling. Stopping it here keeps a pick from also toggling the
        // row's expansion.
        <div className={styles.clickGuard} onClick={(e) => e.stopPropagation()}>
            <Popover open={isOpen} onOpenChange={handleOpenChange} modal={false}>
                <Popover.Trigger asChild>
                    {/* The whole cell is the field, the way the inline editors elsewhere are: it
                        shows its edge on hover and the pencil with it, and sits flat otherwise. */}
                    <button
                        type="button"
                        className={styles.field}
                        aria-label={EDIT_TAGS_LABEL}
                        // The draft is built from the tag list, so until it is in, opening the picker
                        // would start from nothing and the first pick would drop the row's own tags.
                        disabled={areTagsLoading || isSaving}
                    >
                        {items.length ? (
                            <TagChips items={items} />
                        ) : (
                            <span className={styles.noTags}>{NO_TAGS_LABEL}</span>
                        )}
                        <Pencil className={styles.pencil} aria-hidden="true" />
                    </button>
                </Popover.Trigger>
                <Popover.Content
                    align="start"
                    className={styles.content}
                    // The picker's list is portaled too, outside the popover's own content, so a pick
                    // in it reads as a click outside — and would close the popover under the picker.
                    onInteractOutside={(event) => {
                        if (isInside(event.target, PICKER_LIST_SELECTOR)) {
                            event.preventDefault();
                        }
                    }}
                    // A press on a field is the exception: on this one it is the click that closes
                    // the popover, and on another row's it is what opens that picker in turn.
                    onPointerDownOutside={(event) => {
                        if (!isInside(event.target, PICKER_LIST_SELECTOR) && !isInside(event.target, FIELD_SELECTOR)) {
                            swallowNextClick();
                        }
                    }}
                >
                    <ComboboxExternal
                        multiple
                        autoHighlight
                        items={options}
                        isItemEqualToValue={(a, b) => a.value === b.value}
                        value={draft}
                        onValueChange={setDraft}
                        placeholder={SEARCH_PLACEHOLDER}
                        noItemsFound={areTagsLoading ? LOADING : NO_TAGS_FOUND}
                    />
                </Popover.Content>
            </Popover>
        </div>
    );
};

const TagChips = ({ items }: { items: TagItem[] }) => {
    const { ref, overflowingChildren } = useOverflowingChildren<HTMLSpanElement>({
        reserveRight: OVERFLOW_BADGE_RESERVE_PX,
    });

    if (!items.length) {
        return NO_VALUE;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild>
                <span ref={ref} className={styles.chips}>
                    {items.map((item) => (
                        <Badge key={item.value} variant="secondary" className={styles.chip}>
                            {item.label}
                        </Badge>
                    ))}
                    {overflowingChildren.length > 0 && (
                        <Badge data-overflowing="ignore" variant="outline" className={styles.chip}>
                            +{overflowingChildren.length}
                        </Badge>
                    )}
                </span>
            </Tooltip.Trigger>
            <Tooltip.Content side="bottom" className={styles.tooltip}>
                {items.map((item) => item.label).join(', ')}
            </Tooltip.Content>
        </Tooltip>
    );
};
